import * as React from 'react';
import * as styles from './support.module.scss';
import { AppContext } from '../../shared/app-context';
import CircularProgress from '@material-ui/core/CircularProgress';
import useSupport from '../../hooks/useSupport';
import useSearch from '../../hooks/useSearch';
import SearchBar from '../common/search-bar';
import CollapsibleSidebar from '../collapsible-sidebar/collapsible-sidebar';
import { SupportContext } from '../../shared/support-context';
import SupportSidebar from './supportSidebar/support-sidebar';
import useMeasuredRef from '../../hooks/useMeasuredRef';
import SupportPagination from './supportPagination/support-pagination';
import SupportTable from './supportTable/support-table';
import SecondaryButton from '../common/secondary-button';
import { Redirect } from 'react-router-dom';
import Paths from '../../models/enums/paths';

/**
 * Support
 * @function Support - Outer component for the support page
 */
const Support = () => {
  const { state } = React.useContext(AppContext);
  const { selectedFilters, pageSize, totalTickets } = React.useContext(SupportContext);
  const {
    loading,
    tickets,
    fetchSupportTickets,
    fetchSupportInformation,
    orderColumn,
    orderDirection,
    allFilters
  } = useSupport();
  const { searchQuery, updateSearchQuery, setIsSearching } = useSearch();
  const [collapsed, setCollapsed] = React.useState<boolean>(false);

  const announcementCount: number = +!!state.clientAnnouncement + +!!state.globalAnnouncement;
  const { measuredRef, dimensions } = useMeasuredRef([tickets, announcementCount], true);

  const [redirect, setRedirect] = React.useState(false);

  const areSupportFilters = !!Object.values(allFilters).find(filter => filter.length > 0);

  // Fetches the support information for the page on mount
  React.useEffect(() => {
    fetchSupportInformation(state.clientId, { count: pageSize });
  }, []);

  if (redirect) {
    return <Redirect to={{ pathname: Paths.AddRequest, state: { redirect: true, newPath: Paths.Support } }} />;
  }

  return (
    <div className={styles.supportContainer}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Support</h1>
          {allFilters.types.length > 0 && (
            <SecondaryButton text="Add a Request" title="Add a Request" handleClick={() => setRedirect(true)} />
          )}
        </div>
        <SearchBar
          value={searchQuery}
          updateValue={(val: string) => updateSearchQuery(val)}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
          placeholder={'Search Support Requests'}
          submitHandler={() =>
            fetchSupportTickets(state.clientId, {
              ...selectedFilters,
              orderColumn,
              orderDirection,
              count: pageSize,
              searchTerm: searchQuery,
              startAt: 0
            })
          }
        />
      </div>
      {loading && tickets.length === 0 && !areSupportFilters ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : (!tickets || tickets.length === 0) && !areSupportFilters ? (
        <span>There are currently no support requests</span>
      ) : (
        <div
          className={styles.main}
          ref={measuredRef}
          style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
        >
          {(loading && tickets.length > 0) || (loading && tickets.length === 0 && areSupportFilters) ? (
            <div className={styles.circularProgress}>
              <CircularProgress />
            </div>
          ) : null}
          <div className={styles.sidebarContainer}>
            <span className={styles.resultsCount}>{totalTickets} results</span>
            <CollapsibleSidebar
              initialCollapse={false}
              collapsedClass={styles.collapsed}
              sidebarClass={styles.sidebar}
              iconClass={styles.icon}
              arrowOpenIcon={true}
              onCollapseToggle={() => setCollapsed(!collapsed)}
            >
              <SupportSidebar collapsed={collapsed} />
            </CollapsibleSidebar>
          </div>
          <div className={styles.paginatedContent}>
            <SupportTable loading={loading} />
            <SupportPagination />
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
